import { Grid } from '@mui/material'
import { groupBy, orderBy } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import AdminFeatureExplorer from './components/AdminFeatureExplorer'
import useBoolean from 'common/hooks/useBoolean'
import ConfirmModal from 'common/components/modal/ConfirmModal'
import DialogAdminFeature from './components/DialogAdminFeature'
import useTitles from 'common/hooks/useTitles'
import { CardTable } from 'common/components/datatable/Table'
import useActions from 'common/hooks/useActions'
import { NEW } from 'common/components/action/ActionConstant'
import { hasPermission } from 'common/utils/HabilitationUtil'
import { deleteAdminFeature, fetchAdminFeatures, selectAdminFeatures } from 'gdl/adminFeatures/AdminFeaturesSlice'

const AdminFeaturesApp = ({

}) => {
    const dispatch = useDispatch()
    const {
        user,
        adminFeatures,
    } = useSelector(store => ({
        user: store.app.user,
        adminFeatures: store.gdl.adminFeatures,
    }), shallowEqual)
    const listAdminFeatures = useSelector(selectAdminFeatures, shallowEqual)

    useTitles(() => [{
        label: 'Paramètrage licence',
        href: '/settingLicense',
    }], [])

    useEffect(() => {
        dispatch(fetchAdminFeatures())
    }, [dispatch])

    const [selectedAdminFeature, setSelectedAdminFeature] = useState({})
    const {
        value: isDialogOpen,
        setTrue: onOpenDialog,
        setFalse: onCloseDialog,
    } = useBoolean(false)
    const {
        value: isConfirmOpen,
        setTrue: onOpenConfirm,
        setFalse: onCloseConfirm,
    } = useBoolean(false)

    useActions(() => [{
        type: NEW,
        onClick: onOpenDialog,
        displayed: hasPermission(user, 'settingLicenses', 'create'),
    }], [])

    const formattedGroupedAdminFeatures = useMemo(() => {
        const orderedFeatures = orderBy(listAdminFeatures, ['typeFeature', 'code'], ['desc', 'asc'])
        const groupedAdminFeatures = groupBy(orderedFeatures, s => s.parent ?? 'headTree')

        const formatAdminFeatures = (filteredAdminFeatures = []) => {
            return filteredAdminFeatures.map(({ id, code, name, typeFeature, description, pluggedInFrontend }) => {
                const subFeatures = formatAdminFeatures(groupedAdminFeatures[id])
                return {
                    id,
                    label: `[${code}] ${name}`,
                    typeFeature,
                    description,
                    pluggedInFrontend,
                    subFeatures,
                }
            })
        }

        return formatAdminFeatures(groupedAdminFeatures.headTree)
    }, [listAdminFeatures])

    const existInTree = useCallback((id, treeFeatures = []) => {
        return treeFeatures.some(f => f.id === id || existInTree(id, f.subFeatures))
    }, [])

    const adminFeaturesNotInTree = adminFeatures.allIds.filter(id => !existInTree(id, formattedGroupedAdminFeatures))

    const onCreate = id => {
        setSelectedAdminFeature({ parent: id })
        onOpenDialog()
    }
    const onUpdate = id => {
        setSelectedAdminFeature(adminFeatures.byId[id])
        onOpenDialog()
    }
    const onDelete = id => {
        setSelectedAdminFeature({ id })
        onOpenConfirm()
    }

    return (
        <>
            <Grid container spacing={1} alignItems='flex-start' sx={{ padding: '5px 15%' }}>
                {formattedGroupedAdminFeatures.map(adminFeature => (
                    <Grid key={adminFeature.code} size={12}>
                        <AdminFeatureExplorer
                            adminFeature={adminFeature}
                            actions={[
                                {
                                    icon: 'add_circle_outline',
                                    onClick: onCreate,
                                    displayed: hasPermission(user, 'settingLicenses', 'create'),
                                },
                                {
                                    icon: 'edit',
                                    onClick: onUpdate,
                                    displayed: hasPermission(user, 'settingLicenses', 'update'),
                                },
                                {
                                    icon: 'delete_outlined',
                                    onClick: onDelete,
                                    displayed: hasPermission(user, 'settingLicenses', 'delete'),
                                },
                            ]}
                        />
                    </Grid>
                ))}
                {adminFeaturesNotInTree.length > 0 && (
                    <Grid size={12}>
                        <CardTable
                            title={'Fonctionnalité non présente dans l\'arbre'}
                            headers={['id', 'code', 'description', { key: 'idParent', value: 'Id parent' }, { key: 'codeParent', value: 'Code parent' }]}
                            rows={adminFeaturesNotInTree.map(af => ({
                                id: af.id,
                                code: af.code,
                                description: af.description,
                                idParent: af.parent,
                                codeParent: adminFeatures.byId[af.parent]?.code,
                            }))}

                            lineActions={[
                                {
                                    icon: 'edit',
                                    onClick: ({ id }) => {
                                        setSelectedAdminFeature(adminFeatures.byId[id])
                                        onOpenDialog()
                                    },
                                    displayed: hasPermission(user, 'settingLicenses', 'update'),
                                },
                                {
                                    icon: 'delete',
                                    onClick: ({ id }) => {
                                        setSelectedAdminFeature({ id })
                                        onOpenConfirm()
                                    },
                                    displayed: hasPermission(user, 'settingLicenses', 'delete'),
                                },
                            ]}
                        />
                    </Grid>
                )}
            </Grid>
            <DialogAdminFeature
                isOpen={isDialogOpen}
                onClose={() => {
                    setSelectedAdminFeature()
                    onCloseDialog()
                }}
                onValidate={() => dispatch(fetchAdminFeatures())}
                adminFeature={selectedAdminFeature}
            />
            <ConfirmModal
                isOpen={isConfirmOpen}
                title='Êtes-vous sûr de vouloir supprimer cette fonctionnalité ?'
                onValidate={() => {
                    dispatch(deleteAdminFeature(selectedAdminFeature.id)).then(() => {
                        dispatch(fetchAdminFeatures())
                        setSelectedAdminFeature()
                        onCloseConfirm()
                    })
                }}
                onClose={onCloseConfirm}
            />
        </>
    )
}

export default AdminFeaturesApp